import { Form, FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ActionArchiveListItem,
	ActionListItem,
	Button,
	FlexGap,
	GraphQLErrorMessage,
	Heading,
	Paragraph,
	Select,
	SettingsSection,
	Spinner,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import FeedbackTemplateQuestions from '../../../feedback/settings/FeedbackTemplateQuestions';
import DetailsTableForm from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';

function useJobTitleOptions() {
	const { localize } = useLocalize();
	const jobTitles = graphql.useJobTitlesQuery({
		variables: { first: 1000, isArchived: false },
	});

	return jobTitles.data?.jobTitles.edges.map((edge) => ({
		label: localize(edge.node.title).text,
		value: edge.node.id,
	}));
}

function FeedbackTemplateEditor(props: { template?: graphql.FeedbackTemplate }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const history = useHistory();

	const [error, attempt, reset] = useErrorBoundary();

	const [create, { loading: createLoading }] = graphql.useCreateFeedbackTemplateMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateFeedbackTemplateMutation();
	const [duplicate, { loading: duplicateLoading }] = graphql.useCreateFeedbackTemplateMutation();

	const loading = createLoading || updateLoading;

	const formik = useFormik({
		initialValues: {
			title: props.template?.title ?? [],
			description: props.template?.description ?? [],
			instruction: props.template?.instruction ?? [],
			jobTitleIDs: props.template?.jobTitleIDs ?? [],
		},
		onSubmit: () => editOrCreate(),
		enableReinitialize: true,
	});

	const updateTemplate = async (changes: Partial<graphql.UpdateFeedbackTemplateInput>) => {
		await update({
			variables: {
				input: {
					id: props.template!.id,
					roles: props.template!.roles,
					title: formik.values.title.map((string) => ({
						languageID: string.languageID,
						text: string.text,
					})),
					description: formik.values.description,
					instruction: formik.values.instruction,
					jobTitleIDs: formik.values.jobTitleIDs.length > 0 ? formik.values.jobTitleIDs : undefined,
					questions: props.template!.questions.map(({ question, canSubjectView }) => ({
						questionID: question.id,
						instruction: [],
						canSubjectView,
					})),
					...changes,
				},
			},
		});
	};

	const editOrCreate = attempt(async () => {
		reset();

		if (props.template) {
			await updateTemplate({
				title: formik.values.title.map((string) => ({
					languageID: string.languageID,
					text: string.text,
				})),
				description: formik.values.description,
				instruction: formik.values.instruction,
			});

			return;
		}

		const { data } = await create({
			variables: {
				input: {
					title: formik.values.title.map((string) => ({
						languageID: string.languageID,
						text: string.text,
					})),
					description: formik.values.description,
					roles: [graphql.FeedbackTemplateRole.Request],
					questions: [],
				},
			},
			refetchQueries: ['FeedbackTemplates'],
		});

		if (data) {
			history.push(`/settings/feedback/${data.createFeedbackTemplate.feedbackTemplate.id}`);
		}

		addToast(t('common.settings.careers.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const onDuplicate = attempt(async () => {
		reset();

		const template = props.template!;

		const { data } = await duplicate({
			variables: {
				input: {
					title: template.title.map((string) => ({
						languageID: string.languageID,
						text: t('common.copyOf', {
							text: string.text,
							lng: string.languageID.split('_')[0],
						}),
					})),
					description: template.description,
					instruction: template.instruction,
					roles: template.roles,
					isPrivate: template.isPrivate,
					jobTitleIDs: template.jobTitleIDs?.length ? template.jobTitleIDs : undefined,
					questions: template.questions.map(({ question, instruction, canSubjectView }) => ({
						questionID: question.id,
						instruction,
						canSubjectView,
					})),
				},
			},
			refetchQueries: ['FeedbackTemplates'],
			awaitRefetchQueries: true,
		});

		if (data) {
			history.push(`/settings/feedback/${data.createFeedbackTemplate.feedbackTemplate.id}`);
		}
	});

	const formValues = [
		{
			label: t('common.settings.feedbackTemplate.field.title'),
			name: 'title',
			type: DetailsTableFormValueTypes.Multilingual,
		},
		{
			label: t('common.settings.feedbackTemplate.field.description'),
			name: 'description',
			type: DetailsTableFormValueTypes.MultilingualQuill,
		},
	];

	if (props.template) {
		formValues.push({
			label: t('common.settings.feedbackTemplate.field.instruction'),
			name: 'instruction',
			type: DetailsTableFormValueTypes.MultilingualQuill,
		});
	}

	const valid = formik.values.title.some((translation) => translation.text.length > 0);

	const jobTitleOptions = useJobTitleOptions();

	const options = [
		{
			title: t('settings.feedbackLibrary.template.roles.options.cycle'),
			value: [graphql.FeedbackTemplateRole.Cycles].map((role) => role.toString()).join(','),
		},
		{
			title: t('settings.feedbackLibrary.template.roles.options.request'),
			value: [graphql.FeedbackTemplateRole.Cycles, graphql.FeedbackTemplateRole.Request]
				.map((role) => role.toString())
				.join(','),
		},
		{
			title: t('settings.feedbackLibrary.template.roles.options.spontaneous'),
			value: [graphql.FeedbackTemplateRole.Cycles, graphql.FeedbackTemplateRole.Spontaneous]
				.map((role) => role.toString())
				.join(','),
		},
		{
			title: t('settings.feedbackLibrary.template.roles.options.both'),
			value: [
				graphql.FeedbackTemplateRole.Cycles,
				graphql.FeedbackTemplateRole.Request,
				graphql.FeedbackTemplateRole.Spontaneous,
			]
				.map((role) => role.toString())
				.join(','),
		},
		{
			title: t('settings.feedbackLibrary.template.roles.options.growth_path'),
			value: [graphql.FeedbackTemplateRole.GrowthPath].map((role) => role.toString()).join(','),
		},
	];

	const templateIsForGrowthPath = props.template?.roles.includes(
		graphql.FeedbackTemplateRole.GrowthPath,
	);

	return (
		<FlexGap
			flexDirection="column"
			gap={tokens.gap.page}
			pt={`${tokens.gap.page}px`}
			px={4}
			pb={6}
			width="100%"
		>
			<Heading mb={0}>
				{t(`common.settings.feedbackTemplate.${props.template ? 'edit' : 'create'}.title`)}
			</Heading>
			<FlexGap flexDirection="column" gap={tokens.gap.section} width="100%">
				<GraphQLErrorMessage error={error} />
				<FormikProvider value={formik}>
					<Form>
						<FlexGap flexDirection="column" gap={tokens.gap.section} width="100%">
							<SettingsSection first title={t('common.settings.feedbackTemplate.edit.details')}>
								<DetailsTableForm formValues={formValues} />
							</SettingsSection>

							{props.template && (
								<SettingsSection title={t('common.settings.feedbackTemplate.edit.availability')}>
									<DetailsTableForm
										formValues={[
											{
												label: t('common.settings.feedbackTemplate.edit.jobTitleIDs'),
												name: 'jobTitleIDs',
												type: DetailsTableFormValueTypes.MultiSelect,
												options: jobTitleOptions,
											},
										]}
									/>
								</SettingsSection>
							)}
						</FlexGap>
					</Form>
				</FormikProvider>

				<Flex justifyContent="flex-end">
					<Button
						title={t('common.save')}
						onClick={() => formik.submitForm()}
						loading={loading}
						disabled={!valid}
					/>
				</Flex>

				{props.template && (
					<>
						<SettingsSection
							title={t('settings.feedbackLibrary.template.roles.title')}
							description={t('settings.feedbackLibrary.template.roles.description')}
							actions={
								loading ? (
									<Box width={200}>
										<Select.Skeleton />
									</Box>
								) : (
									<Select
										search={false}
										selectedValue={_.sortBy(props.template.roles).join(',')}
										options={options}
										onChange={async (roles) =>
											updateTemplate({ roles: roles!.split(',') as graphql.FeedbackTemplateRole[] })
										}
									/>
								)
							}
						>
							{!templateIsForGrowthPath && (
								<FeedbackTemplateQuestions
									questions={props.template.questions.map(({ question, canSubjectView }) => ({
										question,
										canSubjectView,
									}))}
									onChange={(questions) =>
										updateTemplate({
											questions: questions.map(({ question, canSubjectView }) => ({
												questionID: question.id,
												instruction: [],
												canSubjectView,
											})),
										})
									}
								/>
							)}
							{templateIsForGrowthPath && (
								<Paragraph mb={0}>
									{t('settings.feedbackLibrary.template.questions.notAvailableForGrowthPath')}
								</Paragraph>
							)}
						</SettingsSection>
						<SettingsSection title={t('common.actions')}>
							<ActionArchiveListItem
								archivableItem={props.template}
								archivableItemName={t('common.settings.feedbackTemplates.archive.module.name')}
								archivableItemKey="feedbackTemplate"
								useArchive={graphql.useArchiveFeedbackTemplateMutation}
								useRestore={graphql.useRestoreFeedbackTemplateMutation}
							/>

							<ActionListItem
								label={t('common.settings.feedbackTemplates.duplicate.label')}
								buttonTitle={t('common.settings.feedbackTemplates.duplicate.buttonTitle')}
								onButtonClick={onDuplicate}
								buttonDisabled={loading || duplicateLoading}
							/>
						</SettingsSection>
					</>
				)}
			</FlexGap>
		</FlexGap>
	);
}

function FeedbackTemplateEditorID(props: { id: string }) {
	const { data, loading, error } = graphql.useFeedbackTemplateQuery({
		variables: { id: props.id },
		fetchPolicy: 'network-only',
	});

	if (loading) {
		return (
			<Flex my={4} justifyContent="center">
				<Spinner />
			</Flex>
		);
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <FeedbackTemplateEditor template={data!.feedbackTemplate as graphql.FeedbackTemplate} />;
	}
}

export { FeedbackTemplateEditor as FeedbackTemplateCreator };

export default FeedbackTemplateEditorID;
